/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { observer } from "mobx-react-lite"
import type { FormEvent, JSX } from "react"
import { useEffect, useState } from "react"

import { ApplicationTypeViewModel } from "../../features/application-type/application-type-viewmodel"
import { UserListViewModel } from "../../features/user/user-list-viewmodel"
import { InviteStatus } from "../../features/user/user-types"
import { isValidEmail } from "../../utils/is-valid-email"
import { useViewModel } from "../app/app-hooks"
import { Button } from "../ui-widgets/button/button"

import { ApplicationTypeSelectorTable } from "./application-type-selector-table"

interface UserInviteModalProps {
    orgId: number
    orgName: string
    onClose: () => void
    onSuccess: () => void
}

export const UserInviteModal = observer(({ orgId, orgName, onClose, onSuccess }: UserInviteModalProps): JSX.Element => {
    const appTypeVM = useViewModel(ApplicationTypeViewModel)
    const listVM = useViewModel(UserListViewModel)

    // Load application types when modal is opened
    useEffect(() => {
        const loadData = async () => {
            if (orgId) {
                await appTypeVM.getAppTypes(orgId)
            }
        }
        void loadData()
    }, [appTypeVM, orgId])

    const [username, setUsername] = useState<string>("")
    const [selectedAppTypeIds, setSelectedAppTypeIds] = useState<number[]>([])
    const [formError, setFormError] = useState<string | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)

    // User inputs
    const iUsername = "uUsername"

    const handleSubmit = async (event: FormEvent) => {
        event.stopPropagation()
        event.preventDefault()
        setFormError(null)

        const trimmedUsername = username.trim()

        if (!isValidEmail(trimmedUsername)) {
            setFormError("Please enter a valid email address.")
            return
        }

        setIsSubmitting(true)
        try {
            const invite = await listVM.inviteExternalUser(trimmedUsername, selectedAppTypeIds)
            if (invite === InviteStatus.Success) {
                onSuccess()
            } else {
                setFormError(inviteErrorMessage(invite))
            }
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className="modal block modal-on-background" tabIndex={-1} role="dialog" onClick={onClose}>
            <div
                className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
                role="document"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="modal-content">
                    <div className="modal-header custom-card-header bg-legacy-primary text-white">
                        <h5 className="modal-title">Invite External User</h5>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            aria-label="Close"
                            onClick={onClose}
                        />
                    </div>
                    <div className="modal-body py-legacy">
                        <form onSubmit={handleSubmit} className="grid gap-legacy">
                            {/* Info: Organization target */}
                            {orgName && (
                                <div>
                                    Organization:
                                    <span className="font-semibold text-legacy-dark ms-2">{orgName}</span>
                                </div>
                            )}

                            {/* Email field */}
                            <div className="form-floating">
                                <input
                                    type="text"
                                    id={iUsername}
                                    className={`form-control ${formError ? "is-invalid" : ""}`}
                                    placeholder="Email"
                                    value={username}
                                    onChange={(e) => {
                                        setUsername(e.target.value)
                                        setFormError(null)
                                    }}
                                />
                                <label htmlFor={iUsername}>Email</label>
                                {formError && <div className="invalid-feedback">{formError}</div>}
                            </div>

                            {/* Info: Application type section */}
                            {appTypeVM.hasAllAppTypes && (
                                <div>
                                    <ApplicationTypeSelectorTable
                                        selectedIds={selectedAppTypeIds}
                                        setSelectedIds={setSelectedAppTypeIds}
                                    />
                                </div>
                            )}

                            <div className="modal-footer px-0 pb-0">
                                <Button variant="secondary" onClick={onClose}>
                                    Cancel
                                </Button>
                                <Button type="submit" busy={isSubmitting}>
                                    Invite
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    )
})

const inviteErrorMessage = (status: InviteStatus) => {
    return status === InviteStatus.UserNotFound
        ? "Invitation failed, only existing users can be invited. Please try again."
        : "Invitation failed. Please try again."
}
